import { useEffect, useMemo, useState } from "preact/hooks";
import { Stats, type ProjectionSummary } from "../lib/api";
import { formatMoney } from "@shared/format";
import { currentYear as getCurrentYear } from "@shared/date";
import { describeError } from "../lib/errors";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Máj",
  "Jún",
  "Júl",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dec",
];

export function Projection() {
  const currentYear = getCurrentYear();
  const [year, setYear] = useState<number>(currentYear);
  const [data, setData] = useState<ProjectionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    Stats.projection(year)
      .then(setData)
      .catch((e) => setError(describeError(e)));
  }, [year]);

  const yearOptions = useMemo(() => {
    const ys: number[] = [];
    for (let y = currentYear; y >= currentYear - 4; y--) ys.push(y);
    return ys;
  }, [currentYear]);

  return (
    <main class="app-shell">
      <header class="page__head page__head--stack">
        <h1>Projekcia</h1>
        <p class="muted page__lede">
          Mesačné fakturované sumy a projekcia ročných príjmov voči hranici{" "}
          <strong>€100 000 bez DPH</strong> pre 10% sadzbu dane z príjmov
          právnických osôb (§15 ZDP). Všetky priemery a projekcie sú v sumách
          bez DPH, pokiaľ nie je uvedené inak.
        </p>
      </header>

      <section class="filters">
        <label class="field">
          <span>Rok</span>
          <select
            value={String(year)}
            onChange={(e) =>
              setYear(Number((e.target as HTMLSelectElement).value))
            }
          >
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </section>

      {error && <p class="error">{error}</p>}
      {!data && !error && <p class="muted">Načítavam…</p>}

      {data && (
        <>
          {(() => {
            const bestMonth = data.months.reduce(
              (best, m) => (m.net > best.net ? m : best),
              data.months[0]
            );
            const activeMonths = data.months.filter((m) => m.count > 0).length;
            const safePace = data.thresholdNet / 12;
            const pacePct =
              safePace > 0
                ? Math.round((data.averages.perMonthNet / safePace) * 100)
                : 0;
            const monthsRemaining = Math.max(0, 12 - data.monthsElapsed);
            const requiredPace =
              monthsRemaining > 0
                ? Math.max(0, data.headroom.remainingNet) / monthsRemaining
                : 0;
            return (
              <div class="stat-strip stat-strip--projection">
                <Stat
                  label={`YTD ${data.year} bez DPH`}
                  value={formatMoney(data.totals.net)}
                />
                <Stat
                  label={`YTD ${data.year} s DPH`}
                  value={formatMoney(data.totals.gross)}
                />
                <Stat
                  label="Projekcia roka (bez DPH)"
                  value={formatMoney(data.projection.projectedNet)}
                  {...(data.projection.overThreshold
                    ? { tone: "danger" as const }
                    : {})}
                />
                <Stat
                  label="Faktúr (vystavených)"
                  value={data.totals.count.toLocaleString("sk-SK")}
                />
                <Stat
                  label="Priemer/faktúra (bez DPH)"
                  value={formatMoney(data.averages.perInvoiceNet)}
                />
                <Stat
                  label="Priemer/mesiac (bez DPH)"
                  value={formatMoney(data.averages.perMonthNet)}
                />
                <Stat
                  label={
                    bestMonth && bestMonth.net > 0
                      ? `Najlepší mesiac (${MONTH_LABELS[bestMonth.month - 1]})`
                      : "Najlepší mesiac"
                  }
                  value={
                    bestMonth && bestMonth.net > 0
                      ? formatMoney(bestMonth.net)
                      : "—"
                  }
                />
                <Stat
                  label="Aktívne mesiace"
                  value={`${activeMonths} / 12`}
                />
                <Stat
                  label="Tempo vs. hranica"
                  value={`${pacePct.toLocaleString("sk-SK")} %`}
                  tone={pacePct >= 100 ? "danger" : pacePct >= 80 ? "warn" : "ok"}
                />
                <Stat
                  label={
                    monthsRemaining > 0
                      ? `Tempo do hranice (${monthsRemaining} mes)`
                      : "Tempo do hranice"
                  }
                  value={
                    monthsRemaining > 0
                      ? `${formatMoney(requiredPace)}/mes`
                      : "—"
                  }
                />
              </div>
            );
          })()}

          <Headroom data={data} />

          <ProjectionChart data={data} />

          <section class="projection-table">
            <h2>Mesačný prehľad</h2>
            <table class="table">
              <thead>
                <tr>
                  <th>Mesiac</th>
                  <th class="num">Faktúr</th>
                  <th class="num">Bez DPH</th>
                  <th class="num">DPH</th>
                  <th class="num">S DPH</th>
                  <th class="num">% z hranice</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => {
                  const sharePct =
                    data.thresholdNet > 0
                      ? (m.net / data.thresholdNet) * 100
                      : 0;
                  return (
                    <tr key={m.month}>
                      <td>{MONTH_LABELS[m.month - 1]}</td>
                      <td class="num">{m.count || "—"}</td>
                      <td class="num">
                        {m.net > 0 ? formatMoney(m.net) : "—"}
                      </td>
                      <td class="num">
                        {m.vat > 0 ? formatMoney(m.vat) : "—"}
                      </td>
                      <td class="num">
                        {m.gross > 0 ? formatMoney(m.gross) : "—"}
                      </td>
                      <td class="num">
                        {m.net > 0
                          ? `${sharePct.toLocaleString("sk-SK", {
                              maximumFractionDigits: 1,
                            })} %`
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr class="table__total">
                  <td>
                    <strong>Spolu</strong>
                  </td>
                  <td class="num">
                    <strong>{data.totals.count}</strong>
                  </td>
                  <td class="num">
                    <strong>{formatMoney(data.totals.net)}</strong>
                  </td>
                  <td class="num">
                    <strong>{formatMoney(data.totals.vat)}</strong>
                  </td>
                  <td class="num">
                    <strong>{formatMoney(data.totals.gross)}</strong>
                  </td>
                  <td class="num">
                    <strong>
                      {(
                        (data.totals.net / data.thresholdNet) *
                        100
                      ).toLocaleString("sk-SK", { maximumFractionDigits: 1 })}{" "}
                      %
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>
        </>
      )}
    </main>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "danger";
}) {
  return (
    <div class={tone ? `stat stat--${tone}` : "stat"}>
      <span class="stat__label">{label}</span>
      <span class="stat__value">{value}</span>
    </div>
  );
}

function Headroom({ data }: { data: ProjectionSummary }) {
  const pct = Math.min(100, Math.max(0, data.headroom.utilizationPct));
  const tone =
    pct >= 95 ? "danger" : pct >= 80 ? "warn" : "ok";
  const projOver = data.projection.overThreshold;
  return (
    <section class={`headroom-card headroom-card--${tone}`}>
      <div class="headroom-card__row">
        <div>
          <span class="headroom-card__label">
            Hranica €100 000 bez DPH
            <span class="headroom-card__label-suffix">
              {" "}(§15 ZDP — 10% sadzba CIT)
            </span>
          </span>
          <strong class="headroom-card__value">
            {formatMoney(data.headroom.remainingNet)}{" "}
            <span class="muted">do hranice</span>
          </strong>
        </div>
        <div class="headroom-card__pct">
          <span class="headroom-card__pct-value">
            {pct.toLocaleString("sk-SK", { maximumFractionDigits: 1 })} %
          </span>
          <span class="headroom-card__pct-label">vyčerpané</span>
        </div>
      </div>
      <div class="headroom-card__bar">
        <div
          class="headroom-card__bar-fill"
          style={{ inlineSize: `${pct}%` }}
        />
        <div class="headroom-card__bar-mark" />
      </div>
      <p class="headroom-card__note">
        {projOver ? (
          <>
            <strong>Projekcia roka prekročí hranicu</strong> —{" "}
            {formatMoney(data.projection.projectedNet)} bez DPH pri tempe{" "}
            {formatMoney(data.projection.paceMonthlyNet)}/mes. Zvážte spomalenie
            alebo presun fakturácie do nového roka.
          </>
        ) : (
          <>
            Projekcia roka:{" "}
            <strong>{formatMoney(data.projection.projectedNet)} bez DPH</strong>{" "}
            pri tempe {formatMoney(data.projection.paceMonthlyNet)}/mes (bez
            DPH).
          </>
        )}
      </p>
    </section>
  );
}

function ProjectionChart({ data }: { data: ProjectionSummary }) {
  const safePace = data.thresholdNet / 12;
  const maxBar = Math.max(
    safePace * 1.2,
    ...data.months.map((m) => m.gross)
  );
  const yMax = niceCeil(maxBar);
  const ticks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax];

  const W = 720;
  const H = 280;
  const PAD_L = 60;
  const PAD_R = 16;
  const PAD_T = 16;
  const PAD_B = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const slotW = innerW / 12;
  const barGroupW = slotW * 0.7;
  const barW = barGroupW / 2;
  const yScale = (v: number) => PAD_T + innerH - (v / yMax) * innerH;
  const thresholdY = yScale(safePace);

  return (
    <section class="projection-chart">
      <header class="projection-chart__head">
        <h2>Mesačné fakturované sumy</h2>
        <div class="projection-chart__legend">
          <span class="projection-chart__legend-item">
            <span class="projection-bar projection-bar--net" /> Bez DPH
          </span>
          <span class="projection-chart__legend-item">
            <span class="projection-bar projection-bar--gross" /> S DPH
          </span>
          <span class="projection-chart__legend-item">
            <span class="projection-threshold-marker" /> Bezpečné tempo €
            {Math.round(safePace).toLocaleString("sk-SK")} bez DPH/mes
            (€100&nbsp;000 / 12)
          </span>
        </div>
      </header>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Mesačné fakturované sumy"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              class="projection-chart__grid"
              x1={PAD_L}
              x2={W - PAD_R}
              y1={yScale(t)}
              y2={yScale(t)}
            />
            <text
              class="projection-chart__y-label"
              x={PAD_L - 8}
              y={yScale(t) + 4}
              text-anchor="end"
            >
              {formatCompact(t)}
            </text>
          </g>
        ))}

        {data.months.map((m, i) => {
          const slotX = PAD_L + i * slotW + (slotW - barGroupW) / 2;
          const netH = (m.net / yMax) * innerH;
          const grossH = (m.gross / yMax) * innerH;
          return (
            <g key={m.month}>
              <rect
                class="projection-bar projection-bar--gross"
                x={slotX + barW}
                y={yScale(m.gross)}
                width={barW}
                height={Math.max(0, grossH)}
              >
                <title>
                  {MONTH_LABELS[m.month - 1]} — s DPH:{" "}
                  {formatMoney(m.gross)}
                </title>
              </rect>
              <rect
                class="projection-bar projection-bar--net"
                x={slotX}
                y={yScale(m.net)}
                width={barW}
                height={Math.max(0, netH)}
              >
                <title>
                  {MONTH_LABELS[m.month - 1]} — bez DPH:{" "}
                  {formatMoney(m.net)} ({m.count} fakt.)
                </title>
              </rect>
              <text
                class="projection-chart__x-label"
                x={slotX + barGroupW / 2}
                y={H - PAD_B + 18}
                text-anchor="middle"
              >
                {MONTH_LABELS[m.month - 1]}
              </text>
            </g>
          );
        })}

        <line
          class="projection-threshold"
          x1={PAD_L}
          x2={W - PAD_R}
          y1={thresholdY}
          y2={thresholdY}
        />
        <text
          class="projection-chart__threshold-label"
          x={W - PAD_R - 4}
          y={thresholdY - 6}
          text-anchor="end"
        >
          €{Math.round(safePace).toLocaleString("sk-SK")} bez DPH/mes (10% CIT)
        </text>
      </svg>
    </section>
  );
}

function niceCeil(v: number): number {
  if (v <= 0) return 1000;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * exp;
}

function formatCompact(v: number): string {
  if (v >= 1000) return `${Math.round(v / 1000)}k €`;
  return `${Math.round(v)} €`;
}
